<template>
  <div class="container-fluid py-4">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <h4 class="mb-0">
          <i class="material-icons text-danger me-2"></i> Add New Salary
        </h4>
      </div>
    </div>

    <!-- Card -->
    <div class="row">
      <div class="col-12">
        <div class="card my-4">
          <!-- Form Header -->
          <div
            class="card-header p-3 position-relative mt-n4 mx-3 z-index-2"
            style="background-color:#b03a45;border-radius:12px;"
          >
            <h6 class="text-white text-capitalize mb-0">Salary Information</h6>
          </div>

          <!-- Form -->
          <div class="card-body px-4 pt-4 pb-3">
            <form @submit.prevent="submitForm">
              <div class="row">
                <!-- Title -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">{{ $t('common.labels.title') }}</label>
                  <input
                    v-model="form.title"
                    type="text"
                    class="form-control"
                    :placeholder="$t('common.placeholders.enterSalaryTitle')"
                    required
                  />
                </div>

                <!-- Date (YYYY-MM-DD input -> MM-YYYY API) -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">Date</label>
                  <input
                    v-model="form.date_raw"
                    type="date"
                    class="form-control"
                    required
                  />
                </div>

                <!-- Active (switch) -->
                <div class="col-12 mb-3">
                  <label class="form-label d-block mb-2">Status</label>
                  <div class="form-check form-switch">
                    <input
                      id="active"
                      v-model="form.active"
                      class="form-check-input"
                      type="checkbox"
                      role="switch"
                      style="cursor:pointer;accent-color:#b03a45;"
                    />
                    <label class="form-check-label" for="active">
                      {{ form.active ? 'Active' : 'Inactive' }}
                    </label>
                  </div>
                </div>

                <!-- Document (optional) -->
                <div class="col-12 mb-3">
                  <label class="form-label d-block mb-2">Document (optional)</label>
                  <input
                    ref="fileInputRef"
                    @change="onFileChange"
                    type="file"
                    class="form-control"
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                  />
                  <small class="text-muted">Max 10 MB. Formats: pdf, doc, docx, xls, xlsx.</small>
                  <div v-if="form.document_name" class="mt-2 small">
                    Selected: <strong>{{ form.document_name }}</strong>
                    <button type="button" class="btn btn-link btn-sm" @click="clearFile">Remove</button>
                  </div>
                </div>

                <!-- Actions -->
                <div class="col-12 text-end">
                  <button type="button" class="btn btn-outline-secondary me-2" @click="resetForm" :disabled="isSubmitting">
                    RESET
                  </button>
                  <button
                    type="submit"
                    class="btn text-white"
                    style="background-color:#b03a45;"
                    :disabled="isSubmitting"
                  >
                    <span v-if="isSubmitting">
                      <i class="fas fa-spinner fa-spin me-1" /> Creating...
                    </span>
                    <span v-else>CREATE SALARY</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
          <!-- /Form -->
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/api'
import { useToast } from 'vue-toastification'

const toast = useToast()
const router = useRouter()

// ✅ Adaptez ce chemin si vos routes API diffèrent
// Le contrôleur Laravel semble être un resource controller standard
// Exemples possibles: '/api/salaries' (si prefix api) ou simplement '/salaries'
const SALARY_ENDPOINT = '/financial-requests/salaries'

// 🧭 route de la page liste
const LIST_ROUTE = '/salary'

const isSubmitting = ref(false)
const fileInputRef = ref(null)

const form = ref({
  title: '',
  date_raw: '', // "YYYY-MM-DD" depuis <input type="date">
  active: true,
  document_file: null, // File | null
  document_name: ''
})

// Convertit "YYYY-MM-DD" → "MM-YYYY" (exigé par ton back actuel)
function toMMYYYY(yyyyMMdd) {
  if (!yyyyMMdd) return ''
  const [y, m] = yyyyMMdd.split('-')
  return `${m}-${y}`
}

function onFileChange(e) {
  const file = e.target.files?.[0]
  if (!file) {
    form.value.document_file = null
    form.value.document_name = ''
    return
  }
  // Validation côté front (mimes & taille)
  const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
  const tooBig = file.size > 10 * 1024 * 1024
  if (!allowed.includes(file.type) || tooBig) {
    toast.error('Invalid file. Use pdf/doc/docx/xls/xlsx up to 10MB.')
    clearFile()
    return
  }
  form.value.document_file = file
  form.value.document_name = file.name
}

function clearFile() {
  form.value.document_file = null
  form.value.document_name = ''
  if (fileInputRef.value) fileInputRef.value.value = ''
}

const submitForm = async () => {
  // validations simples
  if (!form.value.title.trim()) {
    toast.error('Title is required')
    return
  }
  if (!form.value.date_raw) {
    toast.error('Please select a date')
    return
  }

  // 🔐 Back attend potentiellement du multipart pour `document_path`
  // et accepte active en true/false/1/0. On envoie "1"/"0" pour être 100% compatible.
  const fd = new FormData()
  fd.append('title', form.value.title)
  fd.append('date', toMMYYYY(form.value.date_raw)) // ex: "08-2025"
  fd.append('active', form.value.active ? '1' : '0')
  if (form.value.document_file) {
    fd.append('document_path', form.value.document_file)
  }

  isSubmitting.value = true
  try {
    await axios.post(SALARY_ENDPOINT, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    toast.success('Monthly salary created successfully')
    router.push(LIST_ROUTE)
  } catch (err) {
    console.error('Error creating salary:', err)
    const apiMsg = err?.response?.data?.message
    // Laravel validation errors
    const laravelErrors = err?.response?.data?.errors
    if (laravelErrors && typeof laravelErrors === 'object') {
      const firstField = Object.keys(laravelErrors)[0]
      const firstMsg = laravelErrors[firstField]?.[0]
      toast.error(firstMsg || apiMsg || 'Failed to create salary')
    } else {
      toast.error(apiMsg || 'Failed to create salary')
    }
  } finally {
    isSubmitting.value = false
  }
}

const resetForm = () => {
  form.value = { title: '', date_raw: '', active: true, document_file: null, document_name: '' }
  if (fileInputRef.value) fileInputRef.value.value = ''
}
</script>

<style scoped>
/* renforce la couleur du switch quand activé */
.form-check-input:checked {
  background-color:#b03a45;
  border-color:#b03a45;
}
</style>
